import { useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { AlertCircle, Check, Copy, FileText } from "lucide-react";

import { Button } from "@app/components/ui/button";
import type { StudyWarning } from "@app/types/study";

/** What the reader must know before trusting a figure: exclusions, masking, small groups. */
export function WarningsList({ warnings }: { warnings: StudyWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {warnings.map((warning) => (
        <li
          key={warning.text}
          className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span>{warning.text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The methods paragraph, as the server wrote it for this exact analysis — the text a manuscript
 * needs, with a button to copy it rather than retype it.
 */
export function MethodsBox({ text }: { text: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <FileText className="h-4 w-4 text-primary" />
          <Trans>Méthodes statistiques</Trans>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void copy()}
          className="h-7 gap-1.5 text-xs hover:border-primary/50"
          aria-label={t`Copier le paragraphe des méthodes`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? <Trans>Copié</Trans> : <Trans>Copier</Trans>}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-slate-600">{text}</p>
    </div>
  );
}
